import { HarmonyLayoutLeft } from "@/components/icons/harmony-icons-inline";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import harmonaiLogo from "@/assets/harmonyai-logo.svg";
import UserCreditsSection from "@/components/common/UserCreditsSection";
import StartNewChatButton from "./StartNewChatButton";
import Navigation from "./Navigation";
import Conversations from "./Conversations";
import SupportButton from "./SupportButton";
import Profile from "./Profile";
import useAuthStore from "@/store/use-auth-store";

/**
 * 🆕 Yeni sidebar iskeleti (akordeon yok):
 * - Üst blok sabit: logo/header + New Chat CTA + navigasyon
 * - Orta blok: sohbet listesi — TEK kaydırılabilir alan (kendi virtual scroll'u)
 * - Alt blok sabit: kredi durumu + destek + profil
 *
 * Böylece "profil açılınca sohbetler kapanıyor" gibi mutually-exclusive
 * akordeon davranışları tamamen ortadan kalkıyor; ChatGPT/Linear tarzı
 * kalıcı bir yerleşim elde ediliyor.
 */
export default function LeftSidebarContent() {
  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useLeftSidebar();
  const { isAuthenticated } = useAuthStore();

  // 🔓 Public mod: guest'te sidebar render edilmez
  // (AppSidebar zaten guest'te tamamen gizli; bu kontrol güvenlik ağı)
  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div
      className={cn(
        "h-full flex flex-col bg-bg-primary dark:bg-gray-900",
        // Desktop: hafif tonlu panel + tek bir sağ kenar çizgisi (kutu-içinde-kutu yok)
        "md:bg-bg-secondary/40 md:dark:bg-gray-900 md:border-r md:border-border-secondary/70 md:dark:border-gray-800"
      )}
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {/* Mobil drawer üst boşluğu (global header'ın altı) */}
      <div className="md:hidden h-3 shrink-0" />

      {/* Header — sadece desktop (mobilde global header var) */}
      <div
        className={cn(
          "hidden md:flex shrink-0 pt-3 pb-2",
          isSidebarOpen
            ? "items-center justify-between pl-3 pr-2"
            : "flex-col items-center gap-1"
        )}
      >
        <Link
          to="/"
          className="flex items-center gap-2 rounded-lg p-1 hover:bg-bg-secondary dark:hover:bg-gray-800 transition-colors focus:outline-none"
          title="HarmonyAI"
        >
          <img src={harmonaiLogo} alt="HarmonyAI" className="h-7 w-7" />
          {isSidebarOpen && (
            <span className="text-[15px] font-bold tracking-tight text-text-primary dark:text-white">
              HarmonyAI
            </span>
          )}
        </Link>

        <button
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          className="size-8 rounded-lg flex items-center justify-center text-text-quaternary dark:text-gray-500 hover:text-text-secondary dark:hover:text-gray-300 hover:bg-bg-secondary dark:hover:bg-gray-800 transition-colors focus:outline-none"
          title="⌘B"
        >
          <HarmonyLayoutLeft size={20} />
        </button>
      </div>

      {/* Sabit üst blok: New Chat + Navigasyon */}
      <div className="shrink-0 flex flex-col gap-2 pt-1 pb-2">
        <StartNewChatButton />
        <Navigation />
      </div>

      {/* İnce ayraç */}
      <div className="shrink-0 mx-3 h-px bg-border-secondary/60 dark:bg-gray-800" />

      {/* Sohbetler — tek kaydırılabilir alan (rail modunda gizli, alan spacer olur) */}
      <div className="flex-1 min-h-0 flex flex-col">
        <Conversations />
      </div>

      {/* Sabit alt blok: krediler + destek + profil */}
      <div className="shrink-0 border-t border-border-secondary/60 dark:border-gray-800 pt-1.5 pb-2">
        <UserCreditsSection />
        <div className="px-2 pt-0.5">
          <SupportButton />
        </div>
        <div className="px-2 pt-0.5">
          <Profile />
        </div>
      </div>
    </div>
  );
}
